import { api } from './api';
import type { Product } from './products.service';

export interface AccessGroup {
  id: string;
  code?: number;
  title: string;
  description?: string;
  accessDuration?: number;
  accessType?: string;
  expirationDate?: string;
  isActive: boolean;
  termsTitle?: string | null;
  termsFileUrl?: string | null;
  products: Product[];
  productsCount: number;
  enrollmentsCount: number;
  integrationsCount?: number;
  gateways?: string[];
  recentEnrollments?: Array<{
    id: string;
    user: { id: string; name: string; email: string; avatar?: string };
    status: string;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface AccessGroupMember {
  id: string;
  user: { id: string; name: string; email: string; avatar?: string };
  progress: number;
  joinedAt: string;
  expiresAt?: string | null;
  status: string;
  createdAt: string;
}

export interface AccessGroupIntegration {
  id: string;
  name: string;
  gateway?: string;
  externalOfferId?: string | null;
  isActive: boolean;
  price?: number;
  currency?: string;
}

export interface AccessGroupIntegrationInput {
  name: string;
  gateway?: string;
  externalOfferId?: string;
  isActive?: boolean;
  price?: number;
  currency?: string;
}

class AccessGroupsService {
  async findAll(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      isActive?: boolean;
    } = {},
  ): Promise<{
    data: AccessGroup[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    const { data } = await api.get(`/access-groups?${searchParams.toString()}`);
    return data;
  }

  async findById(id: string): Promise<AccessGroup> {
    const { data } = await api.get(`/access-groups/${id}`);
    return data.data || data;
  }

  async create(accessGroup: {
    title: string;
    description?: string;
    accessDuration?: number;
    accessType?: string;
    expirationDate?: string;
    isActive?: boolean;
    termsTitle?: string;
    termsFileUrl?: string;
    productIds?: string[];
  }): Promise<AccessGroup> {
    const { data } = await api.post('/access-groups', accessGroup);
    return data.data || data;
  }

  async update(
    id: string,
    accessGroup: {
      title?: string;
      description?: string;
      accessDuration?: number;
      accessType?: string;
      expirationDate?: string;
      isActive?: boolean;
      termsTitle?: string;
      termsFileUrl?: string;
      productIds?: string[];
    },
  ): Promise<AccessGroup> {
    const { data } = await api.put(`/access-groups/${id}`, accessGroup);
    return data.data || data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/access-groups/${id}`);
  }

  async duplicate(id: string): Promise<AccessGroup> {
    const { data } = await api.post(`/access-groups/${id}/duplicate`);
    return data.data || data;
  }

  async addProducts(id: string, productIds: string[]): Promise<AccessGroup> {
    const { data } = await api.post(`/access-groups/${id}/products`, {
      productIds,
    });
    return data.data || data;
  }

  async removeProducts(id: string, productIds: string[]): Promise<AccessGroup> {
    const { data } = await api.delete(`/access-groups/${id}/products`, {
      data: { productIds },
    });
    return data.data || data;
  }

  async getAvailableProducts(id: string): Promise<Product[]> {
    const { data } = await api.get(`/access-groups/${id}/available-products`);
    return data.data || data || [];
  }

  async findByProductId(productId: string): Promise<AccessGroup[]> {
    const { data } = await api.get(`/access-groups/product/${productId}`);
    return data.data || data || [];
  }

  async getMembers(
    id: string,
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    } = {},
  ): Promise<{
    data: AccessGroupMember[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const { data } = await api.get(
      `/access-groups/${id}/members?${searchParams.toString()}`,
    );
    return data;
  }

  async getIntegrations(id: string): Promise<AccessGroupIntegration[]> {
    const { data } = await api.get(`/access-groups/${id}/integrations`);
    return data.data || data || [];
  }

  async createIntegration(
    id: string,
    payload: AccessGroupIntegrationInput,
  ): Promise<AccessGroupIntegration> {
    const { data } = await api.post(
      `/access-groups/${id}/integrations`,
      payload,
    );
    return data.data || data;
  }

  async updateIntegration(
    id: string,
    integrationId: string,
    payload: AccessGroupIntegrationInput,
  ): Promise<AccessGroupIntegration> {
    const { data } = await api.put(
      `/access-groups/${id}/integrations/${integrationId}`,
      payload,
    );
    return data.data || data;
  }

  async deleteIntegration(id: string, integrationId: string): Promise<void> {
    await api.delete(`/access-groups/${id}/integrations/${integrationId}`);
  }
}

export const accessGroupsService = new AccessGroupsService();
